import classnames from 'classnames';
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react';
import { FormattedMessage } from 'react-intl';
import type { To } from 'react-router';
import styled, { css, keyframes } from 'styled-components';

import type { Link, LinkProps } from '@sorare/routing';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { DotsLoader } from '#src/atoms/loader/DotsLoader/index.tsx';
import { BaseLoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { useLoadingForMethod } from '#src/hooks/useLoadingForMethod.ts';
import { glossary } from '#src/lib/glossary.ts';

import type { colors } from './variants.ts';

export type Color = (typeof colors)[number];

const zoomIn = keyframes`
  to {
    transform: scale(1)
  }
`;

/* A transparent border reveals the border-box layer, which is how CSS fakes a gradient stroke. */
const surfaceAndStroke = css`
  border: 1px solid transparent;
  background:
    linear-gradient(var(--pill-surface), var(--pill-surface)) padding-box,
    linear-gradient(
        0deg,
        var(--pill-stroke-from) 0%,
        var(--pill-stroke-via) var(--pill-stroke-via-stop),
        var(--pill-stroke-to) 100%
      )
      border-box;
  box-shadow: inset 0 0 var(--unit) var(--pill-glow);
`;

const hoverOverlayStyle = css`
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--pill-hover-overlay);
`;

const flexCenter = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--unit);
`;

const pillStyle = css`
  --pill-surface: transparent;
  --pill-stroke-from: transparent;
  --pill-stroke-via: transparent;
  --pill-stroke-via-stop: 63%;
  --pill-stroke-to: transparent;
  --pill-glow: transparent;
  --pill-hover-overlay: transparent;
  --pill-icon-size: var(--double-unit);
  --pill-font: var(--t-label-l);
  --pill-font-weight: var(--t-bold);
  --pill-text-transform: none;

  ${flexCenter}
  height: var(--pill-height);
  min-width: var(--pill-height);
  padding: 0 var(--double-unit);
  border-radius: 999px;
  color: var(--c-onBase-primary);
  & {
    font: var(--pill-font);
    font-weight: var(--pill-font-weight);
    text-transform: var(--pill-text-transform);
    letter-spacing: 0;
    white-space: nowrap;
  }
  ${surfaceAndStroke}

  &.large {
    --pill-height: calc(6 * var(--unit));
  }
  &.medium {
    --pill-height: calc(5 * var(--unit));
  }
  &.small {
    --pill-height: var(--quadruple-unit);
  }
  &.compact {
    --pill-height: var(--triple-unit);
    --pill-icon-size: var(--intermediate-unit);
    --pill-font: var(--t-label-m);
    padding: 0 var(--unit);
  }

  &.brand {
    --pill-font: var(--t-brand-s);
    --pill-font-weight: normal;
    --pill-text-transform: uppercase;
    &.compact {
      --pill-font: var(--t-brand-xs);
    }
  }

  &.fullWidth {
    width: 100%;
  }
  &.iconOnly {
    padding: 0 var(--double-unit);
    &.compact {
      padding: 0 var(--intermediate-unit);
    }
  }

  &.primary {
    --pill-surface: var(--c-button-primary-surface);
    --pill-stroke-from: var(--c-unique-secondary);
    --pill-stroke-via: var(--c-unique);
    --pill-stroke-via-stop: 77%;
    --pill-stroke-to: var(--c-button-primary-stroke-to);
    --pill-glow: var(--c-button-primary-glow);
  }
  &.secondary {
    --pill-surface: var(--c-button-secondary-surface);
    --pill-stroke-from: var(--c-common-tertiary);
    --pill-stroke-via: var(--c-nd-150);
    --pill-stroke-to: var(--c-white);
    --pill-glow: var(--c-button-secondary-surface);
    color: var(--c-button-secondary-onSurface);
  }
  &.tertiary {
    --pill-surface: var(--c-black);
    --pill-stroke-from: var(--c-common-tertiary);
    --pill-stroke-via: var(--c-nd-150);
    --pill-stroke-to: var(--c-white);
    --pill-glow: var(--c-button-tertiary-glow);
  }
  &.quaternary {
    --pill-surface: var(--c-nd-50);
    --pill-glow: var(--c-nd-150);
    backdrop-filter: blur(16px);
  }
  &.transparent {
    &:is(button, a):not(:disabled):hover {
      --pill-surface: var(--c-nd-50);
      --pill-glow: var(--c-nd-150);
      backdrop-filter: blur(16px);
    }
  }
  &.destructive {
    --pill-surface: var(--c-rare-tertiary);
    --pill-stroke-from: var(--c-rare-tertiary);
    --pill-stroke-via: var(--c-rare-secondary);
    --pill-stroke-via-stop: 77%;
    --pill-stroke-to: var(--c-button-red-stroke-to);
    --pill-glow: var(--c-rare);
    color: var(--c-red-400);
  }

  &.caution {
    --pill-surface: var(--c-limited-tertiary);
    --pill-stroke-from: var(--c-limited-tertiary);
    --pill-stroke-via: var(--c-limited-tertiary);
    --pill-stroke-to: var(--c-limited-tertiary);
    --pill-glow: var(--c-limited);
    color: var(--c-onBase-caution);
  }
  &.success {
    --pill-surface: var(--c-competition-allStar-tertiary);
    --pill-stroke-from: var(--c-competition-allStar-tertiary);
    --pill-stroke-via: var(--c-competition-allStar-tertiary);
    --pill-stroke-to: var(--c-competition-allStar-tertiary);
    --pill-glow: var(--c-onBase-success);
    color: var(--c-onBase-success);
  }

  &.selected {
    --pill-surface: var(--c-black);
    background: radial-gradient(
        ellipse 56% 86% at 50% 100%,
        rgba(var(--pill-accent-rgb), 0.6) 0%,
        rgba(var(--pill-accent-rgb), 0) 100%
      )
      no-repeat var(--pill-surface);
    border-color: rgb(var(--pill-accent-rgb));
    box-shadow: 0 0 16px -6px rgb(var(--pill-accent-rgb));
  }

  &.primary:is(button, a):not(:disabled):hover {
    --pill-surface: var(--c-button-primary-surface-hovered);
  }
  &.quaternary:is(button, a):not(:disabled):hover {
    --pill-surface: var(--c-nd-100);
  }
  &.destructive:is(button, a):not(:disabled):hover {
    --pill-surface: var(--c-button-red-surface-hovered);
  }
  &.secondary:is(button, a):not(:disabled):hover,
  &.tertiary:is(button, a):not(:disabled):hover,
  &.caution:is(button, a):not(:disabled):hover,
  &.success:is(button, a):not(:disabled):hover,
  &.selected:is(button, a):not(:disabled):hover {
    --pill-hover-overlay: var(--c-nd-50);
  }

  /* :is(button, a) leaves the asSpan variant, which renders a span, inert */
  &:is(button, a):not(:disabled):hover::after {
    ${hoverOverlayStyle}
  }

  &:disabled {
    cursor: default;
    color: var(--c-nd-400);
    backdrop-filter: blur(16px);
    &,
    &.selected {
      --pill-surface: var(--c-nd-50);
      --pill-stroke-from: transparent;
      --pill-stroke-via: transparent;
      --pill-stroke-to: transparent;
      --pill-glow: transparent;
      background: linear-gradient(var(--pill-surface), var(--pill-surface))
        padding-box;
      border-color: transparent;
      box-shadow: none;
    }
  }

  &.blurred {
    backdrop-filter: blur(64px);
  }

  & .pill-cta {
    ${flexCenter}
  }
  & .pill-label {
    ${flexCenter}
  }
  & .pill-loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    transform: scale(0);
  }
  & .pill-loading-indicator {
    font-size: var(--triple-unit);
  }
  & .pill-dots {
    margin-left: var(--unit);
  }
  &.small .pill-loading-indicator {
    font-size: var(--double-unit);
  }
  &.compact .pill-loading-indicator {
    font-size: var(--unit);
  }
  &.loading .pill-loader {
    animation: 0.2s ease-in-out ${zoomIn} both;
  }
  &.loading .pill-cta {
    transition: transform 0.2s ease-in-out;
    transform: scale(0);
  }

  view-transition-class: stretch;
`;

const Pill = styled(ButtonBase)`
  ${pillStyle}
`;

const IconSlot = styled.span`
  display: inline-flex;
  font-size: var(--pill-icon-size);
`;

const childViewTransition = (
  viewTransitionName: string | undefined,
  child: 'icon' | 'label'
) =>
  viewTransitionName
    ? { viewTransitionName: `${viewTransitionName}-${child}` }
    : undefined;

export interface Props extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
  'type' | 'onClick'
> {
  size: 'compact' | 'small' | 'medium' | 'large';
  color?: Color;
  to?: To;
  href?: string;
  replace?: boolean;
  fullWidth?: boolean;
  disableDebounce?: boolean;
  externalLink?: boolean;
  state?: Record<string, unknown>;
  className?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  type?: 'submit' | 'button';
  asSpan?: boolean;
  blur?: boolean;
  prefetch?: LinkProps<typeof Link>['prefetch'];
  ref?: Ref<HTMLElement>;
  loading?: boolean;
  dots?: boolean;
  brand?: boolean;
  selected?: boolean;
  accentRgb?: string;
  viewTransitionName?: string;
  onClick?: (
    e: MouseEvent<HTMLButtonElement>
  ) => void | Promise<void> | Promise<unknown>;
}

export const Button = ({
  children,
  size,
  color = 'primary',
  onClick,
  to,
  href,
  disableDebounce = false,
  externalLink = false,
  className,
  startIcon,
  endIcon,
  type = 'button',
  fullWidth,
  asSpan,
  blur,
  disabled,
  prefetch,
  ref,
  loading: externalLoading,
  dots,
  brand,
  selected,
  accentRgb = 'var(--c-rgb-white)',
  viewTransitionName,
  style: styleProp,
  ...rest
}: Props) => {
  const [internalLoading, wrappedMethod] = useLoadingForMethod(onClick);
  const isLoading = externalLoading || internalLoading;
  const iconOnly = children == null;

  return (
    <Pill
      {...rest}
      ref={ref as Ref<HTMLButtonElement | HTMLAnchorElement>}
      to={disabled ? undefined : to}
      href={disabled ? undefined : href}
      as={asSpan ? 'span' : undefined}
      type={type}
      externalLink={externalLink}
      disableDebounce={disableDebounce}
      prefetch={to ? prefetch : undefined}
      onClick={wrappedMethod}
      disabled={disabled || isLoading}
      className={classnames(color, size, className, {
        brand,
        selected,
        fullWidth,
        iconOnly,
        blurred: blur,
        loading: isLoading,
      })}
      style={
        {
          '--pill-accent-rgb': accentRgb,
          viewTransitionName,
          ...styleProp,
        } as CSSProperties
      }
    >
      {!!isLoading && (
        <span className="pill-loader">
          {dots ? (
            <>
              <FormattedMessage {...glossary.loading} />
              <span className="pill-dots">
                <DotsLoader />
              </span>
            </>
          ) : (
            <span className="pill-loading-indicator">
              <BaseLoadingIndicator />
            </span>
          )}
        </span>
      )}
      <span className="pill-cta">
        {!!startIcon && (
          <IconSlot style={childViewTransition(viewTransitionName, 'icon')}>
            {startIcon}
          </IconSlot>
        )}
        {!iconOnly && (
          <span
            className="pill-label"
            style={childViewTransition(viewTransitionName, 'label')}
          >
            {children}
          </span>
        )}
        {!!endIcon && <IconSlot>{endIcon}</IconSlot>}
      </span>
    </Pill>
  );
};
